<!-- ============================================================
     Calendar view — Direction B (Guided Sheet), Cordell styling
     Replaces calendar-view.component.html
     ============================================================ -->

<div class="cc-schedule">

  <!-- Branded app bar -->
  <header class="cc-appbar">
    <div class="cc-appbar__logo">
      <span class="cc-appbar__mark">CC</span>
      <span class="cc-appbar__type">
        <span class="cc-appbar__name">Cordell &amp; Cordell</span>
        <span class="cc-appbar__sub">Divorce &amp; Family Law</span>
      </span>
    </div>
    <a class="cc-appbar__phone" href="tel:18338254388">(833)&nbsp;825-4388</a>
  </header>

  <!-- Alerts -->
  @if (errorMessage) {
    <div class="cc-alert cc-alert--error">{{ errorMessage }}</div>
  }
  @if (successMessage) {
    <div class="cc-alert cc-alert--success">{{ successMessage }}</div>
  }

  <!-- Loading -->
  @if (isLoading) {
    <div class="cc-loading">
      <div class="cc-spinner"></div>
      <p>Loading…</p>
    </div>
  }

  <!-- Navy context band -->
  <section class="cc-intro">
    <span class="cc-intro__eyebrow">Schedule a Consultation</span>
    <h1 class="cc-intro__title">Choose your time</h1>
    <div class="cc-intro__chips">
      <span class="cc-chip">{{ getDurationText() }}</span>
      <span class="cc-chip">{{ meetingTypeName }}</span>
      <span class="cc-chip">{{ meetingLocationName }}</span>
    </div>
    <p class="cc-intro__who">
      Booking with <strong>{{ user1Email }}</strong> · as <strong>{{ user2Email }}</strong>
    </p>
  </section>

  <!-- Week navigation -->
  <div class="cc-weeknav">
    <button type="button" class="cc-weeknav__arrow" (click)="previousPeriod()" aria-label="Previous week">‹</button>
    <span class="cc-weeknav__range">{{ weekStart | date:'MMM d' }} &ndash; {{ weekEnd | date:'MMM d' }}</span>
    <button type="button" class="cc-weeknav__today" (click)="today()">Today</button>
    <button type="button" class="cc-weeknav__arrow" (click)="nextPeriod()" aria-label="Next week">›</button>
  </div>

  <!-- Day strip -->
  <div class="cc-daystrip" role="tablist">
    @for (day of days; track day.date.toISOString(); let i = $index) {
      <button
        type="button"
        role="tab"
        class="cc-daystrip__day"
        [class.is-active]="i === selectedDayIndex"
        [class.is-empty]="!day.hasAvailable"
        [attr.aria-selected]="i === selectedDayIndex"
        [disabled]="!day.hasAvailable"
        (click)="selectDay(i)">
        <span class="cc-daystrip__dow">{{ day.date | date:'EEE' }}</span>
        <span class="cc-daystrip__num">{{ day.date | date:'d' }}</span>
      </button>
    }
  </div>

  <!-- Selected day -->
  @if (selectedDay; as day) {
    <section class="cc-day">
      <div class="cc-day__head">
        <div class="cc-day__title">
          <span class="cc-day__name">{{ day.date | date:'EEEE' }}</span>
          <span class="cc-day__date">{{ day.date | date:'MMMM d' }}</span>
        </div>
        @if (day.location) {
          <span class="cc-day__loc">{{ day.location }}</span>
        }
      </div>

      @if (day.hasAvailable) {
        @if (day.morning.length) {
          <h2 class="cc-day__group">Morning</h2>
          <div class="cc-slots">
            @for (slot of day.morning; track slot.start.toISOString()) {
              <button
                type="button"
                class="cc-slot"
                [class.is-selected]="isSelectedSlot(slot)"
                [class.is-unavailable]="!slot.available && !slot.bufferBlocked"
                [class.is-buffer]="slot.bufferBlocked"
                [disabled]="!slot.available"
                (click)="selectSlot(slot)">
                {{ slot.start | date:'shortTime' }}
              </button>
            }
          </div>
        }
        @if (day.afternoon.length) {
          <h2 class="cc-day__group">Afternoon</h2>
          <div class="cc-slots">
            @for (slot of day.afternoon; track slot.start.toISOString()) {
              <button
                type="button"
                class="cc-slot"
                [class.is-selected]="isSelectedSlot(slot)"
                [class.is-unavailable]="!slot.available && !slot.bufferBlocked"
                [class.is-buffer]="slot.bufferBlocked"
                [disabled]="!slot.available"
                (click)="selectSlot(slot)">
                {{ slot.start | date:'shortTime' }}
              </button>
            }
          </div>
        }
      } @else {
        <p class="cc-day__empty">No availability on this day.</p>
      }
    </section>
  }

  <!-- Select bar (appears once a slot is chosen).
       Sits before the footer so the desktop sticky bar lands above it;
       on mobile it is position:fixed, so DOM order has no visual effect. -->
  @if (selectedSlotStart && !showAppointmentDialog) {
    <div class="cc-selectbar">
      <div class="cc-selectbar__info">
        <span class="cc-selectbar__label">Selected</span>
        <span class="cc-selectbar__value">
          {{ selectedSlotStart | date:'EEE' }} · {{ selectedSlotStart | date:'shortTime' }}
        </span>
      </div>
      <button type="button" class="cc-btn cc-btn--green" (click)="reviewSelection()">Confirm</button>
    </div>
  }

  <!-- Slim footer CTA -->
  <footer class="cc-foot">
    <p class="cc-foot__line">Questions about your consultation?</p>
    <a class="cc-foot__cta" href="tel:13145873610">Call (314) 587-3610</a>
    <p class="cc-foot__legal">© 2026 Cordell &amp; Cordell. Attorney advertising.</p>
  </footer>

  <!-- Confirm bottom sheet -->
  @if (showAppointmentDialog) {
    <div class="cc-sheet-overlay" (click)="closeAppointmentDialog()">
      <div class="cc-sheet" (click)="$event.stopPropagation()">
        <div class="cc-sheet__grip"></div>
        <span class="cc-sheet__eyebrow">Confirm Appointment</span>
        <h2 class="cc-sheet__title">{{ meetingLocationName }} {{ meetingTypeName }}</h2>

        <div class="cc-sheet__rows">
          <div class="cc-sheet__row">
            <span>When</span>
            <strong>{{ selectedSlotStart | date:'EEE, MMM d' }} · {{ selectedSlotStart | date:'shortTime' }}&ndash;{{ selectedSlotEnd | date:'shortTime' }}</strong>
          </div>
          <div class="cc-sheet__row">
            <span>Duration</span>
            <strong>{{ getDurationText() }}</strong>
          </div>
          <div class="cc-sheet__row">
            <span>Location</span>
            <strong>{{ isInPersonMeeting() ? getLocationForDay(selectedSlotStart!) : meetingLocationName }}</strong>
          </div>
        </div>

        <button type="button" class="cc-btn cc-btn--green cc-btn--block" (click)="bookAppointment()" [disabled]="isLoading">
          {{ isLoading ? 'Booking…' : 'Book Appointment' }}
        </button>
        <button type="button" class="cc-btn cc-btn--text" (click)="closeAppointmentDialog()">Cancel</button>
      </div>
    </div>
  }

</div>
